/**
 * leave-v2-api.ts — API client for the Leave v2 engine-driven endpoints.
 *
 * Base: `/api/v1/leave/*` (Traefik strips /api → Go `/v1/leave`).
 *
 * Design: ZERO rule logic in JS. The frontend submits raw intent and renders
 * the engine's DecisionResponse — all date-math, sandwich, balance, and
 * approval-chain resolution is server-authoritative.
 *
 * Request body fields are snake_case (matching backend JSON tags).
 * Response fields are camelCase where the backend uses camelCase JSON tags
 * (LeaveTypeResponse, BalanceSnapshotEntry) and snake_case where the backend
 * uses snake_case (DecisionResponse, RequestResponse).
 *
 * Contract frozen at: backend-go/internal/modules/leave_v2/dto/contract.go
 *                     backend-go/internal/modules/leave_v2/dto/balance_response.go
 *                     backend-go/internal/modules/leave_v2/dto/leave_type.go
 * Agent B imports from this file — do NOT change method signatures or interface
 * field names without coordinating.
 */

import { BaseAPI } from 'src/providers/base-api';

// ─── Input types ──────────────────────────────────────────────────────────────

/**
 * Per-calendar-day intent for a leave request.
 * portion must be one of: "full" | "first_half" | "second_half" | "hours".
 * hours is required when portion="hours" and start_time/end_time are NOT given.
 * For permission-type leaves: supply start_time + end_time (HH:MM 24h) instead of hours;
 * the backend will derive the hours from the time window.
 */
export interface DayInput {
  date: string;        // "YYYY-MM-DD"
  portion: string;     // "full" | "first_half" | "second_half" | "hours"
  hours?: number;      // required when portion="hours" and no start_time/end_time
  start_time?: string; // "HH:MM" 24h — when given with end_time, backend derives hours
  end_time?: string;   // "HH:MM" 24h
}

/**
 * Standard permission sub-types for leave_type_code === "permission".
 * Mirrors Go const dto.StandardPermissionTypes.
 */
export const PERMISSION_TYPES = [
  'Late Coming',
  'Permission',
  'Leaving Early',
  'Continue WFH',
] as const;

export type PermissionType = typeof PERMISSION_TYPES[number];

/**
 * The "Leaving Early" permission sub-type. Referenced by UI logic that keys off
 * a specific sub-type (e.g. hiding the "Back to Work" action, which is
 * meaningless once someone has left early). Kept here next to PERMISSION_TYPES
 * so call sites use a named constant instead of a duplicated magic string; must
 * stay in sync with PERMISSION_TYPES / Go dto.StandardPermissionTypes.
 */
export const PERMISSION_TYPE_LEAVING_EARLY: PermissionType = 'Leaving Early';

/**
 * Body for previewRequest and createRequest.
 * Backend tags: employee_id, leave_type_code, start_date, end_date, days,
 *               reason, sandwich_ack, is_self_requested, auto_approve, lop_override,
 *               permission_type.
 */
export interface LeaveRequestBody {
  employee_id: number;
  leave_type_code: string;
  start_date: string;        // "YYYY-MM-DD"
  end_date: string;          // "YYYY-MM-DD"
  days?: DayInput[];
  reason?: string;
  sandwich_ack?: boolean;
  is_self_requested?: boolean;
  auto_approve?: boolean;    // honoured only for admin callers (backend enforces)
  lop_override?: boolean;    // honoured only when auto_approve=true + admin (backend enforces)
  /** For leave_type_code === "permission": one of PERMISSION_TYPES. Default "Permission". */
  permission_type?: string;
  /** Set to the request ID being edited so the preview conflict check excludes it. */
  edit_request_id?: number;
  /** True when submitted via the Quick Link WFH/Emergency dialogs — gates an immediate Mattermost notification on create. */
  is_quick_link?: boolean;
}

/**
 * Body for POST /requests/:id/approve.
 * Backend tags: comments, waive_sandwich, lop_override.
 */
export interface ApprovalActionBody {
  comments?: string;
  waive_sandwich?: boolean;
  lop_override?: boolean;
}

/**
 * Body for POST /requests/:id/decline.
 */
export interface DeclineActionBody {
  comments?: string;
}

// ─── Output types ─────────────────────────────────────────────────────────────

/**
 * Per-day engine annotation in DecisionResponse and RequestResponse.
 * Backend tags: date, portion, counts_as, is_weekend, is_holiday, is_sandwich,
 *               start_time, end_time.
 */
export interface DayBreakdown {
  date: string;
  portion: string;
  counts_as: number;
  is_weekend: boolean;
  is_holiday: boolean;
  is_sandwich: boolean;
  /** "HH:MM" 24h — populated for permission-type leaves. */
  start_time?: string;
  /** "HH:MM" 24h — populated for permission-type leaves. */
  end_time?: string;
  is_cancelled?: boolean;
  /** cancel_requested=true means a cancellation request is pending manager/admin approval.
   *  The balance has NOT yet been restored. The day still counts toward leave. */
  cancel_requested?: boolean;
  cancel_requested_by?: number;
  cancel_reason?: string;
}

/**
 * One element of required_approvers in DecisionResponse.
 * Backend tags: level, mode, party_ids.
 */
export interface ApproverChainDTO {
  level: string;
  mode?: string;
  party_ids?: number[];
}

/**
 * Engine evaluation surfaced to the client.
 * Returned by previewRequest (no persist) and embedded in RequestResponse.decisions[].
 * Backend tags: effect, total_days, adjusted_days, reasons, required_approvers, days, balance_after,
 *               excess_disposition.
 */
export interface DecisionResponse {
  effect: string;                          // "allow" | "warn" | "requires_approval" | "block"
  total_days: number;
  adjusted_days: number;
  reasons?: string[];
  required_approvers?: ApproverChainDTO[];
  days?: DayBreakdown[];
  balance_after: number;
  /**
   * Populated when the employee's available balance is less than the
   * requested days: the request is NOT blocked — paid_days will be deducted
   * from balance and lop_days will be recorded as Loss of Pay (unpaid) once
   * approved. Both are omitted (undefined) when balance fully covers the request.
   */
  paid_days?: number;
  lop_days?: number;

  /**
   * Present only for hour-unit (permission) leave types when the
   * permission_limits rule allowed a period-cap overflow through instead of
   * blocking it. One of "allow_negative" | "mark_as_lop" | "penalty".
   * Absent for ordinary leave types and for requests within cap — treat
   * absence the same as the pre-existing behavior (insufficient-balance/LOP
   * warning based on balance_after < 0).
   */
  excess_disposition?: string;
}

/**
 * One approval chain row in a RequestResponse.
 * Backend tags: level, mode, approver_party_id, status, acted_at, audit_reason.
 */
export interface ApprovalDTO {
  level: number;
  mode: string;
  approver_party_id: number;
  approver_name?: string;
  status: string;
  acted_at?: string;
  audit_reason?: string;
}

/**
 * Full view of an lv2_request.
 * Backend tags: id, status, employee_id, employee_name, employee_photo_url,
 *               leave_type_code, start_date, end_date, total_days, reason,
 *               admin_comments, is_self_requested, days, decisions, approvals.
 */
export interface RequestResponse {
  id: number;
  status: string;
  employee_id?: number;
  employee_name?: string;
  /**
   * Profile picture asset nano ID for the employee.
   * Pass to BaseAPI.buildProfilePictureURL(employee_photo_url) to get the
   * public streaming URL. Omitted when the employee has no profile picture.
   */
  employee_photo_url?: string;
  leave_type_code: string;
  start_date: string;
  end_date: string;
  total_days: number;
  reason?: string;
  /** Note left by the approver/HR when acting on the request. Only present when non-empty. */
  admin_comments?: string;
  is_self_requested: boolean;
  /** For leave_type_code === "permission": the sub-type selected by the employee. */
  permission_type?: string;
  /** True when created by the Late Coming permission auto-apply cron rather than the employee. */
  is_auto_applied?: boolean;
  /**
   * ISO-8601 timestamp when the employee confirmed their return via the
   * "Back to Work" action (permission-type requests only). Omitted until set.
   */
  back_to_work_at?: string;
  /**
   * ISO-8601 timestamp when the leave request was submitted (from lv2_request.created_at).
   * Used by the Team Leave list "Applied" column. Omitted for legacy records.
   */
  created_at?: string;
  days?: DayBreakdown[];
  decisions?: DecisionResponse[];
  approvals?: ApprovalDTO[];
}

/**
 * createRequest minimal response (id, status, total_days — same shape as RequestResponse).
 */
export interface CreateRequestResponse {
  id: number;
  status: string;
  total_days: number;
}

/**
 * One entry in the balance list — matches BalanceSnapshotEntry (camelCase).
 * Backend tags: leaveTypeId, leaveTypeCode, leaveTypeName, isActive, balance, earned,
 *               used, carriedForward, pending, updatedAt.
 */
export interface BalanceResponse {
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  /** True when the leave type is active; false for soft-deleted / deactivated types. */
  isActive: boolean;
  balance: number;
  earned: number;
  used: number;
  carriedForward: number;
  pending: number;
  updatedAt: string;
}

/**
 * Response envelope for GET /v1/leave/balances.
 * Backend tags: employeeId, balances.
 */
export interface BalanceListResponse {
  employeeId: number;
  balances: BalanceResponse[];
}

/**
 * One leave type — matches LeaveTypeResponse (camelCase).
 * Backend tags: id, code, name, unit, isPaid, requiresBalance, requiresApproval,
 *               allowsPartialDay, isCompensatory, isEncashable, isActive, isSystem,
 *               displayOrder, workday_hours, createdAt, updatedAt.
 */
export interface LeaveType {
  id: number;
  code: string;
  name: string;
  unit: string;            // "day" | "hour"
  isPaid: boolean;
  requiresBalance: boolean;
  requiresApproval: boolean;
  allowsPartialDay: boolean;
  isCompensatory: boolean;
  isEncashable: boolean;
  isActive: boolean;
  isSystem: boolean;
  displayOrder: number;
  /** Gender eligibility restriction: 'male' | 'female' | null (any). */
  eligibleGender?: string | null;
  /** ISO2 country codes this leave type is available in. [] = every country. */
  countryIso2?: string[];
  /**
   * For unit='hour' types only: how many hours equal one leave-day.
   * Used by the backend to convert hours↔days. Default 8.
   * Returned by the server; also sent in create/update payloads when unit='hour'.
   */
  workday_hours?: number;
  createdAt: string;
  updatedAt: string;
}

/** One selectable country option for leave-type country availability. */
export interface TenantCountryOption {
  iso2: string;
  name: string;
  emoji: string;
}

// ─── List query / response types ─────────────────────────────────────────────

/**
 * Query parameters for GET /v1/leave/requests.
 * All are optional except scope.
 */
export interface LeaveListParams {
  /**
   * scope=reports: the caller's DIRECT REPORTS only (all statuses), authorized
   * purely on the reporting relationship (employee.immediate_supervisor_partyId),
   * not on role — any authenticated user may request it; non-supervisors simply
   * get an empty list. Use this instead of scope=all for team leads/managers who
   * are not HR/Admin, to avoid over-exposing the whole tenant's leave data.
   */
  scope: 'mine' | 'team' | 'reports' | 'all';
  /** 'pending' covers submitted + pending_approval server-side. Omit = all. */
  status?: 'pending' | 'approved' | 'cancelled' | 'declined' | 'availed';
  /** leave_type_code, e.g. "casual" */
  leave_type?: string;
  /** "YYYY-MM-DD" — filters start_date >= from */
  from?: string;
  /** "YYYY-MM-DD" — filters start_date <= to */
  to?: string;
  /** Full-text search: name / #id / reason */
  q?: string;
  /** 1-based page number (default 1) */
  page?: number;
  /** Page size (default 25, max 500) */
  limit?: number;
  sort?: string;
  /**
   * Narrow to a single employee's requests (e.g. scope='all' + employee_id
   * when an admin/manager is viewing a specific employee's existing bookings
   * before applying leave on their behalf). Requires the caller to already
   * have scope='all'-level access — see the backend handler doc.
   */
  employee_id?: number;
}

/** Per-status counts in the paginated response envelope. */
export interface LeaveStatusCounts {
  all: number;
  pending: number;
  approved: number;
  cancelled: number;
  declined: number;
  availed: number;
}

/** Paginated response from GET /v1/leave/requests. */
export interface LeaveListResponse {
  data: RequestResponse[];
  total: number;
  page: number;
  limit: number;
  status_counts: LeaveStatusCounts;
}

// ─── On-leave / coverage types ───────────────────────────────────────────────

/**
 * One entry returned by GET /v1/leave/on-leave.
 * Backend tags: employee_id, employee_name, start_date, end_date, leave_type_code.
 */
export interface OnLeaveEntry {
  employee_id: number;
  employee_name: string;
  start_date: string;   // "YYYY-MM-DD"
  end_date: string;     // "YYYY-MM-DD"
  leave_type_code: string;
}

/**
 * Response envelope for GET /v1/leave/on-leave.
 * Backend tags: from, to, count, entries.
 */
export interface OnLeaveResponse {
  from: string;
  to: string;
  count: number;
  entries: OnLeaveEntry[];
}

// ─── Balance Admin types ──────────────────────────────────────────────────────

/**
 * Response for POST /v1/leave/balances/:employee/adjust
 * and POST /v1/leave/balances/:employee/opening.
 * Backend tags: employeeId, eventId, balanceAfter.
 */
export interface AdjustBalanceResponse {
  employeeId: number;
  eventId: number;
  balanceAfter: BalanceResponse;
}

/**
 * One row in the team balance view (one employee × one leave type).
 * Backend tags: employeeId, employeeName, employeeCode, department,
 *               leaveTypeId, leaveTypeCode, leaveTypeName,
 *               balance, earned, used, carriedForward, pending, updatedAt.
 */
export interface TeamBalanceEntry {
  employeeId: number;
  employeeName: string;
  employeeCode: string;
  department: string;
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  balance: number;
  earned: number;
  used: number;
  carriedForward: number;
  pending: number;
  updatedAt: string;
}

/**
 * Paginated response for GET /v1/leave/balances/team.
 * Backend tags: total, page, limit, entries.
 */
export interface TeamBalanceResponse {
  total: number;
  page: number;
  limit: number;
  entries: TeamBalanceEntry[];
}

/** Query parameters for GET /v1/leave/balances/team. */
export interface TeamBalanceParams {
  q?: string;       // employee name / code search
  type?: string;    // leave_type_code filter
  status?: 'active' | 'inactive' | 'all'; // employee.is_active filter (default: active)
  page?: number;
  limit?: number;
}

// ─── Year History balance types ───────────────────────────────────────────────

/**
 * Aggregated balance activity for one leave type in one calendar year.
 * Backend tags: leaveTypeId, leaveTypeCode, leaveTypeName, year,
 *               carryIn, earned, adjustments, used, lapsed, closingBalance.
 */
export interface YearBalanceSummary {
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  year: number;
  /** Balance carried forward into this year. */
  carryIn: number;
  /** Total accrued during this year (accrual + comp-off earn). */
  earned: number;
  /** Net manual adjustments during this year (positive or negative). */
  adjustments: number;
  /** Total consumed during this year (always positive). */
  used: number;
  /** Total lapsed / expired during this year (always positive). */
  lapsed: number;
  /** carryIn + earned + adjustments − used − lapsed */
  closingBalance: number;
}

/**
 * Response for GET /v1/leave/balances/:employee/history?year=YYYY
 * Backend tags: employeeId, year, balances.
 */
export interface YearHistoryResponse {
  employeeId: number;
  year: number;
  balances: YearBalanceSummary[];
}

// ─── Policy Template types (Phase 0 — admin read + instantiate) ──────────────

/**
 * Brief version entry within a template list response.
 * Mirrors dto.PolicyTemplateVersionSummary (camelCase backend tags).
 */
export interface PolicyTemplateVersionSummary {
  id: number;
  version: number;
  effectiveFrom: string;  // "YYYY-MM-DD" or ""
  effectiveTo: string;    // "YYYY-MM-DD" or ""
  status: string;         // "draft" | "published" | "archived"
  notes: string;
}

/**
 * One template item in GET /v1/leave/templates.
 * Mirrors dto.PolicyTemplateResponse.
 */
export interface PolicyTemplateResponse {
  id: number;
  name: string;
  countryCode: string;
  stateCode: string;   // "" when null
  description: string;
  isActive: boolean;
  versions: PolicyTemplateVersionSummary[];
  createdAt: string;
  updatedAt: string;
}

/**
 * One leave type row within a template version.
 * Mirrors dto.PolicyTemplateLeaveTypeResponse.
 */
export interface PolicyTemplateLeaveTypeRow {
  id: number;
  code: string;
  name: string;
  unit: string;             // "day" | "hour"
  isPaid: boolean;
  requiresBalance: boolean;
  requiresApproval: boolean;
  allowsPartialDay: boolean;
  isCompensatory: boolean;
  isEncashable: boolean;
  displayOrder: number;
  defaultConfig: Record<string, unknown>;
}

/**
 * One rule row within a template version.
 * Mirrors dto.PolicyTemplateRuleResponse.
 */
export interface PolicyTemplateRuleRow {
  id: number;
  leaveTypeCode: string;  // "" = global rule
  kind: string;
  params: Record<string, unknown>;
  effect: string;
  severity: number;
  priority: number;
  enabled: boolean;
}

/**
 * Detailed version — version summary + leave types + rules.
 * Mirrors dto.PolicyTemplateVersionDetail.
 */
export interface PolicyTemplateVersionDetail extends PolicyTemplateVersionSummary {
  leaveTypes: PolicyTemplateLeaveTypeRow[];
  rules: PolicyTemplateRuleRow[];
}

/**
 * Full template detail — template + all version details.
 * Mirrors dto.PolicyTemplateDetailResponse.
 */
export interface PolicyTemplateDetailResponse extends PolicyTemplateResponse {
  versions: PolicyTemplateVersionDetail[];
}

/**
 * GET /v1/leave/templates/resolve response.
 * Mirrors dto.ResolveResponse.
 */
export interface PolicyTemplateResolveResponse {
  templateId: number;
  templateName: string;
  countryCode: string;
  stateCode: string;
  version: PolicyTemplateVersionDetail;
}

/**
 * POST /v1/leave/templates/:versionId/instantiate response.
 * Mirrors dto.InstantiateSummary.
 */
export interface PolicyTemplateInstantiateSummary {
  leaveTypesCreated: number;
  leaveTypesUpdated: number;
  rulesCreated: number;
  rulesUpdated: number;
  linkId: number;
}

// ─── Comp-Off Earn request types ─────────────────────────────────────────────

/**
 * Body for POST /api/v1/leave/compoff/earn (employee submits a comp-off earn request).
 * Backend tags: employee_id, worked_date, days, reason, expiry_months.
 */
export interface CompOffEarnRequestBody {
  /** Leave blank for self-submit; required when admin submits on behalf. */
  employee_id?: number;
  /** "YYYY-MM-DD" — the calendar day the employee worked. Must not be in the future. */
  worked_date: string;
  /** 0.5 for half-day or 1.0 for full day. Defaults to 1.0 if omitted. */
  days?: 0.5 | 1.0;
  reason?: string;
  /** Months until the earned lot expires. 0 → server defaults to 4. */
  expiry_months?: number;
  /**
   * Optional comp-off leave type code (e.g. "COMPOFF"). When omitted the
   * server uses the first active compensatory leave type (back-compat default).
   * Set this when the tenant has more than one compensatory leave type.
   */
  leave_type_code?: string;
}

/**
 * Body for approve/decline actions on a comp-off earn request.
 */
export interface CompOffEarnActionBody {
  comments?: string;
}

/**
 * Full view of one lv2_compoff_earn_request row.
 * Backend tags: id, employee_id, employee_name, worked_date, days, reason, admin_comments,
 *               status, expiry_months, balance_event_id, submitted_at, acted_at.
 */
export interface CompOffEarnResponse {
  id: number;
  employee_id: number;
  employee_name?: string;  // resolved from party JOIN — present in admin inbox responses
  worked_date: string;   // "YYYY-MM-DD"
  days: number;
  reason?: string;
  admin_comments?: string;
  status: 'submitted' | 'approved' | 'declined' | 'cancelled';
  expiry_months: number;
  balance_event_id?: number;
  submitted_at: string;  // RFC3339
  acted_at?: string;     // RFC3339, null until admin acts
}

/**
 * Paginated list response for comp-off earn requests.
 * Backend tags: data, total, page, limit.
 */
export interface CompOffEarnListResponse {
  data: CompOffEarnResponse[];
  total: number;
  page: number;
  limit: number;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const LeaveV2API = {
  /**
   * Preview a leave request without persisting it.
   * POST /api/v1/leave/requests/preview
   * Returns DecisionResponse — the engine's full evaluation.
   */
  async previewRequest(
    body: LeaveRequestBody
  ): Promise<{ status: number; data?: DecisionResponse; message?: string }> {
    const response = await BaseAPI.request('POST', 'v1/leave/requests/preview', body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Create a leave request (persisted).
   * POST /api/v1/leave/requests
   * Returns the created request (id, status, total_days).
   */
  async createRequest(
    body: LeaveRequestBody
  ): Promise<{ status: number; data?: RequestResponse; message?: string }> {
    const response = await BaseAPI.request('POST', 'v1/leave/requests', body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Update an existing leave request (re-runs the engine on edit).
   * PATCH /api/v1/leave/requests/:id
   * Editable only when status is submitted/pending_approval — returns 409 otherwise.
   * Returns the updated RequestResponse.
   */
  async updateRequest(
    id: number | string,
    body: LeaveRequestBody
  ): Promise<{ status: number; data?: RequestResponse; message?: string }> {
    const response = await BaseAPI.request('PATCH', `v1/leave/requests/${id}`, body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Unified server-side filtered + paginated leave request list.
   * GET /api/v1/leave/requests
   * Returns LeaveListResponse { data, total, page, limit, status_counts }.
   */
  async listRequests(params: LeaveListParams): Promise<{
    status: number;
    data?: LeaveListResponse;
    message?: string;
  }> {
    // Build query-param object — omit undefined keys so BaseAPI doesn't send empty params
    const qp: Record<string, string | number> = { scope: params.scope };
    if (params.status)     qp['status']     = params.status;
    if (params.leave_type) qp['leave_type'] = params.leave_type;
    if (params.from)       qp['from']       = params.from;
    if (params.to)         qp['to']         = params.to;
    if (params.q)          qp['q']          = params.q;
    if (params.page  !== undefined) qp['page']  = params.page;
    if (params.limit !== undefined) qp['limit'] = params.limit;
    if (params.sort)       qp['sort']       = params.sort;
    if (params.employee_id !== undefined) qp['employee_id'] = params.employee_id;

    const response = await BaseAPI.request(
      'GET',
      'v1/leave/requests',
      null,
      undefined,
      'application/json',
      qp
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * List the current user's own leave requests (server-driven, paginated).
   * Wraps listRequests({ scope: 'mine', ...params }).
   */
  async listMyRequests(params?: Omit<LeaveListParams, 'scope'>): Promise<{
    status: number;
    data?: LeaveListResponse;
    message?: string;
  }> {
    return LeaveV2API.listRequests({ scope: 'mine', ...params });
  },

  /**
   * List a SPECIFIC employee's leave requests — e.g. for an admin/manager
   * applying leave on an employee's behalf, to show that employee's already
   * booked dates on the calendar. Requires the caller to have scope='all'
   * level access (manager/HR/tenant admin/super admin); the backend narrows
   * the tenant-wide scope='all' result down to this one employee.
   * Wraps listRequests({ scope: 'all', employee_id: employeeId, ...params }).
   */
  async listRequestsForEmployee(
    employeeId: number,
    params?: Omit<LeaveListParams, 'scope' | 'employee_id'>
  ): Promise<{
    status: number;
    data?: LeaveListResponse;
    message?: string;
  }> {
    return LeaveV2API.listRequests({ scope: 'all', employee_id: employeeId, ...params });
  },

  /**
   * Approver inbox — pending requests for the current user.
   * Passes status:'pending' server-side so the server filters; no client-side
   * status check needed.
   *
   * scope=team  → requests where current user is in the required_approvers chain
   * scope=all   → all tenant requests (admin view)
   */
  async listForApproval(scope: 'team' | 'all' = 'team', params?: Omit<LeaveListParams, 'scope' | 'status'>): Promise<{
    status: number;
    data?: LeaveListResponse;
    message?: string;
  }> {
    return LeaveV2API.listRequests({ scope, status: 'pending', ...params });
  },

  /**
   * Get a single leave request by ID.
   * GET /api/v1/leave/requests/:id
   * Returns the full RequestResponse including days, decisions, approvals.
   */
  async getRequest(
    id: number | string
  ): Promise<{ status: number; data?: RequestResponse; message?: string }> {
    const response = await BaseAPI.request('GET', `v1/leave/requests/${id}`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Cancel an entire leave request.
   * POST /api/v1/leave/requests/:id/cancel
   */
  async cancelRequest(
    id: number | string
  ): Promise<{ status: number; message?: string }> {
    const response = await BaseAPI.request('POST', `v1/leave/requests/${id}/cancel`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Mark a permission request (Late Coming / Leaving Early / Permission /
   * Continue WFH) as "back to work" — self-service only, available once the
   * request is pending or approved. Threads a Mattermost reply onto the
   * request's original notification post server-side.
   * POST /api/v1/leave/requests/:id/back-to-work
   */
  async backToWork(
    id: number | string
  ): Promise<{ status: number; data?: RequestResponse; message?: string }> {
    const response = await BaseAPI.request('POST', `v1/leave/requests/${id}/back-to-work`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Read-only dry-run of cancelDay: reports which additional auto-added
   * sandwich days (if any) would also be cancelled, without persisting
   * anything. Call this before cancelDay to decide whether to show a
   * "Breaking Sandwich Leave" confirmation.
   * GET /api/v1/leave/requests/:id/days/:date/cancel-preview
   */
  async previewCancelDay(
    id: number | string,
    date: string
  ): Promise<{
    status: number;
    message?: string;
    will_auto_cancel_dates?: string[];
    old_total_days?: number;
    new_total_days?: number;
  }> {
    const response = await BaseAPI.request(
      'GET',
      `v1/leave/requests/${id}/days/${date}/cancel-preview`
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Cancel a single day within a leave request.
   * POST /api/v1/leave/requests/:id/days/:date/cancel
   * date format: "YYYY-MM-DD"
   *
   * For APPROVED leaves where the caller is a non-admin owner, the response
   * will include { pending: true } — the cancellation is queued for approval.
   * For admin callers or pending leaves, the cancellation is immediate
   * and { pending: false } is returned.
   *
   * auto_cancelled_dates lists any additional sandwich days (weekends/
   * holidays no longer bracketed by leave on both sides) that were also
   * cancelled (or queued for cancel, when pending=true) alongside the
   * requested date.
   */
  async cancelDay(
    id: number | string,
    date: string
  ): Promise<{ status: number; message?: string; pending?: boolean; auto_cancelled_dates?: string[] }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/requests/${id}/days/${date}/cancel`
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Approve a pending day-cancel request (approver/admin only).
   * POST /api/v1/leave/requests/:id/days/:date/cancel-approve
   * Performs the actual cancellation: sets cancelled_at, adjusts total_days,
   * writes usage_reversal balance event. Balance is restored HERE.
   */
  async approveDayCancel(
    id: number | string,
    date: string,
    body?: { comments?: string }
  ): Promise<{ status: number; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/requests/${id}/days/${date}/cancel-approve`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Decline a pending day-cancel request (approver/admin only).
   * POST /api/v1/leave/requests/:id/days/:date/cancel-decline
   * Clears the pending-cancel marker; the day remains on leave.
   */
  async declineDayCancel(
    id: number | string,
    date: string,
    body?: { comments?: string }
  ): Promise<{ status: number; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/requests/${id}/days/${date}/cancel-decline`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Approve a leave request.
   * POST /api/v1/leave/requests/:id/approve
   * Supports waive_sandwich and lop_override flags for approver overrides.
   */
  async approve(
    id: number | string,
    body?: ApprovalActionBody
  ): Promise<{ status: number; data?: RequestResponse & { code?: string; available?: number; required?: number; lopDays?: number }; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/requests/${id}/approve`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Decline a leave request.
   * POST /api/v1/leave/requests/:id/decline
   */
  async decline(
    id: number | string,
    body?: DeclineActionBody
  ): Promise<{ status: number; data?: RequestResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/requests/${id}/decline`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * List leave balances for an employee (or the current user when omitted).
   * GET /api/v1/leave/balances  (optionally ?employee=<id>)
   * Returns BalanceListResponse { employeeId, balances[] }.
   */
  async listBalances(employeeId?: number): Promise<{
    status: number;
    data?: BalanceListResponse;
    message?: string;
  }> {
    const params = employeeId !== undefined ? { employee: employeeId } : undefined;
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/balances',
      null,
      undefined,
      'application/json',
      params
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Year-aggregated balance history for one employee.
   * GET /api/v1/leave/balances/:employee/history?year=YYYY
   * When employeeId is omitted, the backend resolves the caller's own employee ID.
   * Returns YearHistoryResponse with per-type summaries (earned, used, carryIn, closing, …).
   */
  async getBalanceHistory(
    employeeId: number,
    year?: number
  ): Promise<{ status: number; data?: YearHistoryResponse; message?: string }> {
    const params: Record<string, string | number> = {};
    if (year !== undefined) params['year'] = year;
    const response = await BaseAPI.request(
      'GET',
      `v1/leave/balances/${employeeId}/history`,
      null,
      undefined,
      'application/json',
      Object.keys(params).length > 0 ? params : undefined
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * List all active leave types for the current tenant.
   * GET /api/v1/leave/types
   * Returns LeaveType[].
   */
  async listLeaveTypes(): Promise<{
    status: number;
    data?: LeaveType[];
    message?: string;
  }> {
    const response = await BaseAPI.request('GET', 'v1/leave/types');
    if (!response) return { status: 0, message: 'No response from server' };
    // The /types endpoint wraps its array in { data: [...] } (unlike /requests,
    // which returns a bare array). Normalize so callers always get LeaveType[].
    const body = response.data as unknown;
    const arr = Array.isArray(body)
      ? (body as LeaveType[])
      : (body as { data?: LeaveType[] } | undefined)?.data;
    return { status: response.status, data: arr, message: response.message };
  },

  /**
   * List leave types the current caller (or a specified employee, for
   * apply-on-behalf) is eligible for (apply-picker view).
   * GET /api/v1/leave/types?for_apply=true (optionally &employee=<id>)
   * Returns LeaveType[]. Use this ONLY for the leave-apply picker — all other
   * consumers (management/config screens) should use listLeaveTypes() for the
   * full catalog.
   */
  async listEligibleLeaveTypes(employeeId?: number): Promise<{
    status: number;
    data?: LeaveType[];
    message?: string;
  }> {
    const params: Record<string, string> =
      employeeId !== undefined
        ? { for_apply: 'true', employee: String(employeeId) }
        : { for_apply: 'true' };
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/types',
      null,
      undefined,
      'application/json',
      params
    );
    if (!response) return { status: 0, message: 'No response from server' };
    // The /types endpoint wraps its array in { data: [...] } (unlike /requests,
    // which returns a bare array). Normalize so callers always get LeaveType[].
    const body = response.data as unknown;
    const arr = Array.isArray(body)
      ? (body as LeaveType[])
      : (body as { data?: LeaveType[] } | undefined)?.data;
    return { status: response.status, data: arr, message: response.message };
  },

  /**
   * List selectable countries for leave-type country availability (admin-gated).
   * GET /api/v1/leave/types/countries
   * Returns TenantCountryOption[].
   */
  async listTenantCountries(): Promise<{
    status: number;
    data?: TenantCountryOption[];
    message?: string;
  }> {
    const response = await BaseAPI.request('GET', 'v1/leave/types/countries');
    if (!response) return { status: 0, message: 'No response from server' };
    const body = response.data as unknown;
    const arr = Array.isArray(body)
      ? (body as TenantCountryOption[])
      : (body as { data?: TenantCountryOption[] } | undefined)?.data;
    return { status: response.status, data: arr, message: response.message };
  },

  /**
   * Create a new leave type for the current tenant.
   * POST /api/v1/leave/types
   * Returns the created LeaveType.
   */
  async createLeaveType(
    body: Omit<LeaveType, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<{ status: number; data?: LeaveType; message?: string }> {
    const response = await BaseAPI.request('POST', 'v1/leave/types', body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Update an existing leave type.
   * PUT /api/v1/leave/types/:id
   * Returns the updated LeaveType.
   * Note: isSystem is read-only on the backend (ignored if sent).
   */
  async updateLeaveType(
    id: number,
    body: Omit<LeaveType, 'id' | 'isSystem' | 'createdAt' | 'updatedAt'>
  ): Promise<{ status: number; data?: LeaveType; message?: string }> {
    const response = await BaseAPI.request('PUT', `v1/leave/types/${id}`, body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Soft-delete a leave type (system types cannot be deleted).
   * DELETE /api/v1/leave/types/:id
   */
  async deleteLeaveType(
    id: number
  ): Promise<{ status: number; message?: string }> {
    const response = await BaseAPI.request('DELETE', `v1/leave/types/${id}`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  // ─── Balance Admin (Feature 1, 2, 3) ─────────────────────────────────────────

  /**
   * Manual balance adjustment (HR/admin only).
   * POST /api/v1/leave/balances/:employee/adjust
   * Body: { leave_type_code, amount (signed, non-zero), reason, effective_date? }
   * Returns AdjustBalanceResponse { employeeId, eventId, balanceAfter }.
   */
  async adjustBalance(
    employeeId: number,
    body: { leave_type_code: string; amount: number; reason: string; effective_date?: string }
  ): Promise<{ status: number; data?: AdjustBalanceResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/balances/${employeeId}/adjust`,
      body
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Set opening balance for an employee (HR/admin only, one-time per employee+type).
   * POST /api/v1/leave/balances/:employee/opening
   * Body: { leave_type_code, amount, as_of_date?, note? }
   * Returns AdjustBalanceResponse { employeeId, eventId, balanceAfter }.
   * Returns 409 if opening balance already exists for this employee+type.
   */
  async setOpeningBalance(
    employeeId: number,
    body: { leave_type_code: string; amount: number; as_of_date?: string; note?: string }
  ): Promise<{ status: number; data?: AdjustBalanceResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/balances/${employeeId}/opening`,
      body
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Team / all-employee balance view (HR/admin only).
   * GET /api/v1/leave/balances/team
   * Returns TeamBalanceResponse { total, page, limit, entries[] }.
   */
  async listTeamBalances(params?: TeamBalanceParams): Promise<{
    status: number;
    data?: TeamBalanceResponse;
    message?: string;
  }> {
    const qp: Record<string, string | number> = {};
    if (params?.q)           qp['q']     = params.q;
    if (params?.type)        qp['type']  = params.type;
    if (params?.status)      qp['status'] = params.status;
    if (params?.page  !== undefined) qp['page']  = params.page;
    if (params?.limit !== undefined) qp['limit'] = params.limit;

    const response = await BaseAPI.request(
      'GET',
      'v1/leave/balances/team',
      null,
      undefined,
      'application/json',
      qp
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Fetch requests for the team calendar, bounded to a month range.
   * GET /api/v1/leave/requests?scope=…&from=…&to=…&limit=500
   *
   * Managers pass scope=all; regular employees pass scope=mine.
   * Pass from/to as the month's first and last day (YYYY-MM-DD) so the
   * server returns only requests that overlap the visible month.
   *
   * Each RequestResponse includes days[] (DayBreakdown) so the calendar
   * can use days.filter(d => d.counts_as > 0) without any range expansion.
   */
  async listForCalendar(
    scope: 'all' | 'reports' | 'mine' = 'all',
    range?: { from: string; to: string },
  ): Promise<{
    status: number;
    data?: LeaveListResponse;
    message?: string;
  }> {
    return LeaveV2API.listRequests({
      scope,
      from:  range?.from,
      to:    range?.to,
      limit: 500,
    });
  },

  // ─── Policy Templates (Phase 0 — admin read + instantiate) ───────────────────

  /**
   * List all active templates (with version summaries).
   * GET /api/v1/leave/templates
   * Requires Tenant Admin or Super Admin.
   * Returns { data: PolicyTemplateResponse[] }
   */
  async listPolicyTemplates(): Promise<{
    status: number;
    data?: PolicyTemplateResponse[];
    message?: string;
  }> {
    const response = await BaseAPI.request('GET', 'v1/leave/templates');
    if (!response) return { status: 0, message: 'No response from server' };
    // Unwrap envelope: { data: [...] }
    const body = response.data as unknown;
    const arr = Array.isArray(body)
      ? (body as PolicyTemplateResponse[])
      : (body as { data?: PolicyTemplateResponse[] } | undefined)?.data ?? [];
    return { status: response.status, data: arr, message: response.message };
  },

  /**
   * Get full template detail (all versions + leave types + rules for each version).
   * GET /api/v1/leave/templates/:id
   */
  async getPolicyTemplate(id: number | string): Promise<{
    status: number;
    data?: PolicyTemplateDetailResponse;
    message?: string;
  }> {
    const response = await BaseAPI.request('GET', `v1/leave/templates/${id}`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Resolve which template version applies for the given country/state/date.
   * GET /api/v1/leave/templates/resolve?country=IN&state=&as_of=YYYY-MM-DD
   */
  async resolvePolicyTemplate(params: {
    country: string;
    state?: string;
    as_of?: string;
  }): Promise<{
    status: number;
    data?: PolicyTemplateResolveResponse;
    message?: string;
  }> {
    const qp: Record<string, string> = { country: params.country };
    if (params.state)  qp['state']  = params.state;
    if (params.as_of)  qp['as_of']  = params.as_of;
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/templates/resolve',
      null,
      undefined,
      'application/json',
      qp
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Instantiate a template version into the caller's tenant (MUTATING).
   * POST /api/v1/leave/templates/:versionId/instantiate
   * Body: { asOfDate?: "YYYY-MM-DD" }
   * Returns InstantiateSummary { leaveTypesCreated, leaveTypesUpdated, rulesCreated, rulesUpdated, linkId }
   *
   * ⚠️  This is a real mutation — it upserts lv2_leave_type and lv2_leave_policy_rule rows
   * for the caller's tenant. Always show a confirmation dialog before calling this.
   */
  async instantiatePolicyTemplate(
    versionId: number | string,
    body?: { asOfDate?: string }
  ): Promise<{
    status: number;
    data?: PolicyTemplateInstantiateSummary;
    message?: string;
  }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/templates/${versionId}/instantiate`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Get employees on approved/availed leave overlapping a date range.
   * GET /api/v1/leave/on-leave?from=YYYY-MM-DD&to=YYYY-MM-DD
   * Manager-gated — returns 403 for non-managers.
   * Returns OnLeaveResponse { from, to, count, entries[] }.
   */
  async getOnLeave(from: string, to: string): Promise<{
    status: number;
    data?: OnLeaveResponse;
    message?: string;
  }> {
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/on-leave',
      null,
      undefined,
      'application/json',
      { from, to }
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  // ─── Comp-Off Earn (request/approve/decline) ──────────────────────────────

  /**
   * Employee submits a comp-off earn request for a day they worked.
   * POST /api/v1/leave/compoff/earn
   * For self-submit: omit employee_id (resolved from JWT by server).
   * Admin-on-behalf: supply employee_id (backend enforces admin gate).
   * Returns CompOffEarnResponse (status="submitted").
   */
  async submitCompOffEarnRequest(
    body: CompOffEarnRequestBody
  ): Promise<{ status: number; data?: CompOffEarnResponse; message?: string }> {
    const response = await BaseAPI.request('POST', 'v1/leave/compoff/earn', body);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * List the current employee's own comp-off earn requests (paginated).
   * GET /api/v1/leave/compoff/earn
   * Returns CompOffEarnListResponse { data, total, page, limit }.
   */
  async listMyCompOffEarnRequests(params?: { page?: number; limit?: number }): Promise<{
    status: number;
    data?: CompOffEarnListResponse;
    message?: string;
  }> {
    const qp: Record<string, number> = {};
    if (params?.page  !== undefined) qp['page']  = params.page;
    if (params?.limit !== undefined) qp['limit'] = params.limit;
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/compoff/earn',
      null,
      undefined,
      'application/json',
      qp
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Admin/HR inbox: comp-off earn requests filtered by status.
   * GET /api/v1/leave/compoff/earn/inbox?status=<status>
   * status: "submitted" (default) | "approved" | "declined" | "cancelled" | "expired" | "all"
   * Requires admin/HR role (backend enforces).
   */
  async listCompOffEarnInbox(params?: { page?: number; limit?: number; status?: string }): Promise<{
    status: number;
    data?: CompOffEarnListResponse;
    message?: string;
  }> {
    const qp: Record<string, string | number> = {};
    if (params?.page   !== undefined) qp['page']   = params.page;
    if (params?.limit  !== undefined) qp['limit']  = params.limit;
    if (params?.status !== undefined) qp['status'] = params.status;
    const response = await BaseAPI.request(
      'GET',
      'v1/leave/compoff/earn/inbox',
      null,
      undefined,
      'application/json',
      qp
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Get a single comp-off earn request.
   * GET /api/v1/leave/compoff/earn/:id
   */
  async getCompOffEarnRequest(id: number | string): Promise<{
    status: number;
    data?: CompOffEarnResponse;
    message?: string;
  }> {
    const response = await BaseAPI.request('GET', `v1/leave/compoff/earn/${id}`);
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Admin/HR approves a submitted comp-off earn request.
   * POST /api/v1/leave/compoff/earn/:id/approve
   * On approval the server calls GrantCompOff → writes a compoff_earn balance event.
   * Returns updated CompOffEarnResponse (status="approved", balance_event_id set).
   */
  async approveCompOffEarnRequest(
    id: number | string,
    body?: CompOffEarnActionBody
  ): Promise<{ status: number; data?: CompOffEarnResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/compoff/earn/${id}/approve`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Admin/HR declines a submitted comp-off earn request (no balance change).
   * POST /api/v1/leave/compoff/earn/:id/decline
   * Returns updated CompOffEarnResponse (status="declined").
   */
  async declineCompOffEarnRequest(
    id: number | string,
    body?: CompOffEarnActionBody
  ): Promise<{ status: number; data?: CompOffEarnResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/compoff/earn/${id}/decline`,
      body ?? {}
    );
    return response ?? { status: 0, message: 'No response from server' };
  },

  /**
   * Employee cancels their own pending comp-off earn request.
   * POST /api/v1/leave/compoff/earn/:id/cancel
   * Returns updated CompOffEarnResponse (status="cancelled").
   */
  async cancelCompOffEarnRequest(
    id: number | string
  ): Promise<{ status: number; data?: CompOffEarnResponse; message?: string }> {
    const response = await BaseAPI.request(
      'POST',
      `v1/leave/compoff/earn/${id}/cancel`
    );
    return response ?? { status: 0, message: 'No response from server' };
  },
};
